import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Upload, Trash2, BookOpen, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, CardHeader, CardTitle, Button, Input, Label, FormGroup,
  Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText, EmptyHint,
  Badge, Flex
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const PAGE_HINTS_KEYS = [
  { value: '',                    key: 'general' },
  { value: '/dashboard',          key: 'dashboard' },
  { value: '/connectors',         key: 'connectors' },
  { value: '/agents-config',      key: 'agentsConfig' },
  { value: '/callcenter',         key: 'callcenter' },
  { value: '/monitor',            key: 'monitor' },
  { value: '/knowledge',          key: 'knowledge' },
  { value: '/team',               key: 'team' },
  { value: '/billing',            key: 'billing' },
  { value: '/templates',          key: 'templates' },
  { value: '/tasks',              key: 'tasks' },
  { value: '/manager',            key: 'manager' },
  { value: '/sallus/dashboard',   key: 'sallusDashboard' },
  { value: '/sallus/marketplace', key: 'sallusMarketplace' },
  { value: '/sallus/analytics',   key: 'sallusAnalytics' },
  { value: '/sallus/settings',    key: 'sallusSettings' },
];

const HelpAdmin = () => {
  const { t } = useTranslation('help');
  const toast   = useToast();
  const confirm = useConfirm();

  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ title: '', section: '', pageHint: '', text: '' });
  const [expandedId, setExpandedId] = useState(null);
  const textRef = useRef(null);

  useEffect(() => { loadDocs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocs = async () => {
    try {
      const res = await api.get('/help/documents');
      setDocs(res.data.documents || []);
    } catch (e) {
      toast.error(t('loadError', { msg: e.response?.data?.message || e.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.text.trim() || !form.title.trim() || !form.section.trim()) {
      toast.warning(t('fillRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/help/documents', form);
      toast.success(t('indexSuccess'));
      setForm({ title: '', section: '', pageHint: '', text: '' });
      setShowForm(false);
      loadDocs();
    } catch (e) {
      toast.error(t('indexError', { msg: e.response?.data?.message || e.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (docId, title) => {
    const ok = await confirm({ title: t('removeConfirm'), message: t('removeConfirmMsg', { title }) });
    if (!ok) return;
    try {
      await api.delete(`/help/documents/${docId}`);
      toast.success(t('removeSuccess'));
      setDocs(prev => prev.filter(d => d.docId !== docId));
    } catch (e) {
      toast.error(t('removeError', { msg: e.response?.data?.message || e.message }));
    }
  };

  const grouped = docs.reduce((acc, doc) => {
    const key = doc.section || 'Geral';
    if (!acc[key]) acc[key] = [];
    acc[key].push(doc);
    return acc;
  }, {});

  const getPageLabel = (value) => {
    const hint = PAGE_HINTS_KEYS.find(p => p.value === value);
    return hint ? t(`pageHints.${hint.key}`) : value;
  };

  return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>📖 {t('title')}</PageTitle>
            <PageSubtitle>
              {t('subtitle')}
              {docs.length > 0 && ` ${t('subtitleCount', { count: docs.length })}`}
            </PageSubtitle>
          </div>
          <Button onClick={() => setShowForm(v => !v)}>
            <Plus size={16} /> {t('addChunk')}
          </Button>
        </PageHeader>

        {showForm && (
          <Card style={{ marginBottom: theme.spacing[6] }}>
            <CardHeader>
              <CardTitle>{t('newChunkTitle')}</CardTitle>
            </CardHeader>
            <FormGrid>
              <FormGroup>
                <Label>{t('titleField')}</Label>
                <Input
                  placeholder={t('titlePlaceholder')}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>{t('sectionField')}</Label>
                <Input
                  placeholder={t('sectionPlaceholder')}
                  value={form.section}
                  onChange={e => setForm(f => ({ ...f, section: e.target.value }))}
                />
              </FormGroup>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>{t('pageHintField')}</Label>
                <select
                  value={form.pageHint}
                  onChange={e => setForm(f => ({ ...f, pageHint: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: theme.radii.md,
                    border: `1px solid ${theme.colors.border}`, fontSize: theme.typography.sm,
                    fontFamily: theme.typography.fontFamily, background: theme.colors.white
                  }}
                >
                  {PAGE_HINTS_KEYS.map(p => (
                    <option key={p.value} value={p.value}>{t(`pageHints.${p.key}`)}</option>
                  ))}
                </select>
              </FormGroup>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>{t('contentField')}</Label>
                <Textarea
                  ref={textRef}
                  placeholder={t('contentPlaceholder')}
                  value={form.text}
                  onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                  rows={8}
                />
                <CharCount>{t('charCount', { count: form.text.length })}</CharCount>
              </FormGroup>
            </FormGrid>
            <Flex gap={theme.spacing[3]} style={{ marginTop: theme.spacing[4] }}>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <><Spinner size="16px" /> {t('indexing')}</> : <><Upload size={16} /> {t('indexBtn')}</>}
              </Button>
              <Button variant="secondary" onClick={() => setShowForm(false)}>{t('cancel')}</Button>
            </Flex>
          </Card>
        )}

        {loading ? (
          <LoadingContainer><Spinner /><span>{t('loading')}</span></LoadingContainer>
        ) : docs.length === 0 ? (
          <EmptyState>
            <EmptyIcon><BookOpen size={40} strokeWidth={1} /></EmptyIcon>
            <EmptyText>{t('noChunks')}</EmptyText>
            <EmptyHint>{t('noChunksHint')}</EmptyHint>
          </EmptyState>
        ) : (
          Object.entries(grouped).map(([section, items]) => (
            <SectionBlock key={section}>
              <SectionTitle>
                <BookOpen size={16} /> {section}
                <Badge variant="neutral" style={{ marginLeft: theme.spacing[2] }}>{items.length}</Badge>
              </SectionTitle>
              {items.map(doc => (
                <DocRow key={doc.docId}>
                  <DocMain onClick={() => setExpandedId(expandedId === doc.docId ? null : doc.docId)}>
                    <DocTitle>{doc.title}</DocTitle>
                    <DocMeta>
                      {doc.pageHint && <Badge variant="info">{getPageLabel(doc.pageHint)}</Badge>}
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                    </DocMeta>
                    {expandedId === doc.docId ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </DocMain>
                  {expandedId === doc.docId && (
                    <DocPreview>
                      <DocPreviewText>{doc.text || t('noPreview')}</DocPreviewText>
                    </DocPreview>
                  )}
                  <DocActions>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.docId, doc.title)}>
                      <Trash2 size={14} /> {t('removeBtn')}
                    </Button>
                  </DocActions>
                </DocRow>
              ))}
            </SectionBlock>
          ))
        )}
      </PageContainer>
    </>
  );
};

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const Textarea = styled.textarea`
  width: 100%; padding: ${theme.spacing[3]}; font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm}; color: ${theme.colors.textPrimary};
  background: ${theme.colors.white}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; resize: vertical; outline: none; line-height: 1.6;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;
const CharCount = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; text-align: right; margin-top: 4px;`;
const SectionBlock = styled.div`margin-bottom: ${theme.spacing[5]};`;
const SectionTitle = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary}; margin-bottom: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} 0; border-bottom: 1px solid ${theme.colors.border};
`;
const DocRow = styled.div`
  background: ${theme.colors.white}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg}; margin-bottom: ${theme.spacing[2]};
  overflow: hidden;
`;
const DocMain = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]}; cursor: pointer;
  &:hover { background: ${theme.colors.gray50}; }
`;
const DocTitle = styled.div`flex: 1; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const DocMeta = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const DocPreview = styled.div`padding: ${theme.spacing[3]} ${theme.spacing[4]}; background: ${theme.colors.gray50}; border-top: 1px solid ${theme.colors.border};`;
const DocPreviewText = styled.pre`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; white-space: pre-wrap; word-break: break-word; margin: 0; font-family: ${theme.typography.fontFamily};`;
const DocActions = styled.div`display: flex; justify-content: flex-end; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border-top: 1px solid ${theme.colors.border}; background: ${theme.colors.gray50};`;

export default HelpAdmin;
